
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { polishPlurals } from 'polish-plurals';
import {
    createRawPreview,
    DEFAULT_RAW_DEVELOP_SETTINGS,
    hasEmbeddedJpegPreview,
    isRawFile,
    RawDevelopSettings,
} from '@/service/rawConverter';
import {
    CANCELLED_RAW_CONVERSION,
    RawConversionDecision,
    rawConversionConfirmationBus,
} from '@/service/rawConversionConfirmation';

interface ConfirmationRequest {
    files: File[];
    rawCount: number;
    resolve: (decision: RawConversionDecision) => void;
}

const RawSliderLabel = Vue.extend({
    functional: true,
    props: {
        label: String,
        value: [String, Number],
    },
    render(h, context) {
        return h('div', { class: 'raw-slider-label' }, [
            h('span', context.props.label),
            h('strong', String(context.props.value)),
        ]);
    },
});

@Component({
    components: {
        RawSliderLabel,
    },
})
export default class RawConversionDialog extends Vue {
    private polishPlurals = polishPlurals;
    private dialogOpen = false;
    private phase: 'confirm' | 'advanced' = 'confirm';
    private advanced = false;
    private applyToAll = false;
    private finalizing = false;
    private rawCount = 0;
    private rawFiles: File[] = [];
    private currentIndex = 0;
    private settingsByFile = new Map<File, RawDevelopSettings>();
    private pendingResolve: ((decision: RawConversionDecision) => void) | null = null;
    private queuedRequests: ConfirmationRequest[] = [];
    private previewUrl = '';
    private previewError = '';
    private previewLoading = false;
    private previewTimer: number | null = null;
    private previewRequestId = 0;
    private syncingAll = false;
    private previewZoom = 1;
    private previewPanX = 0;
    private previewPanY = 0;
    private previewDragging = false;
    private previewDragStartX = 0;
    private previewDragStartY = 0;
    private previewDragOriginX = 0;
    private previewDragOriginY = 0;
    private advancedOptionsOpen = false;
    private technicalOptionsOpen = false;
    private checkingEmbeddedPreviews = false;

    private colorSpaces = [
        { text: 'sRGB', value: 1 },
        { text: 'Adobe RGB', value: 2 },
        { text: 'Wide Gamut RGB', value: 3 },
        { text: 'ProPhoto RGB', value: 4 },
        { text: 'XYZ', value: 5 },
        { text: 'ACES', value: 6 },
    ];
    private highlightModes = [
        { value: 0, text: 'Standardowe przycięcie do bieli' },
        { value: 1, text: 'Zachowaj nieprzycięte światła' },
        { value: 2, text: 'Połącz przycięte i nieprzycięte światła' },
        { value: 3, text: 'Odbudowa świateł — poziom 1' },
        { value: 4, text: 'Odbudowa świateł — poziom 2' },
        { value: 5, text: 'Odbudowa świateł — poziom 3' },
        { value: 6, text: 'Odbudowa świateł — poziom 4' },
        { value: 7, text: 'Odbudowa świateł — poziom 5' },
        { value: 8, text: 'Odbudowa świateł — poziom 6' },
        { value: 9, text: 'Odbudowa świateł — poziom 7' },
    ];
    private qualityModes = Array.from({ length: 13 }, (_, value) => ({
        value,
        text: `${value}${value === 3 ? ' — domyślna' : ''}`,
    }));
    private fbddModes = [
        { text: 'Wyłączone', value: 0 },
        { text: 'Lekkie', value: 1 },
        { text: 'Pełne', value: 2 },
    ];
    private cameraMatrixModes = [
        { text: 'Wyłączona', value: 0 },
        { text: 'Gdy używany jest balans bieli', value: 1 },
        { text: 'Zawsze', value: 3 },
    ];
    private fujiRotateModes = [
        { text: 'Według pliku RAW', value: -1 },
        { text: 'Wyłączony', value: 0 },
        { text: 'Włączony', value: 1 },
    ];

    mounted() {
        rawConversionConfirmationBus.$on('request', this.enqueue);
        window.addEventListener('mousemove', this.movePreview);
        window.addEventListener('mouseup', this.stopPreviewDrag);
    }

    beforeDestroy() {
        rawConversionConfirmationBus.$off('request', this.enqueue);
        window.removeEventListener('mousemove', this.movePreview);
        window.removeEventListener('mouseup', this.stopPreviewDrag);
        this.clearPreviewTimer();
        this.revokePreview();
        if (this.pendingResolve) this.pendingResolve(CANCELLED_RAW_CONVERSION);
        this.queuedRequests.forEach(({ resolve }) => resolve(CANCELLED_RAW_CONVERSION));
    }

    get currentFile(): File | undefined {
        return this.rawFiles[this.currentIndex];
    }

    get currentSettings(): RawDevelopSettings {
        if (!this.currentFile) return this.cloneDefaults();
        return this.settingsByFile.get(this.currentFile) || this.cloneDefaults();
    }

    get whiteBalanceMode(): 'camera' | 'auto' | 'none' {
        if (this.currentSettings.useAutoWb) return 'auto';
        if (this.currentSettings.useCameraWb) return 'camera';
        return 'none';
    }

    set whiteBalanceMode(value: 'camera' | 'auto' | 'none') {
        this.$set(this.currentSettings, 'useAutoWb', value === 'auto');
        this.$set(this.currentSettings, 'useCameraWb', value === 'camera');
    }

    get autoBrightness(): boolean {
        return !this.currentSettings.noAutoBright;
    }

    set autoBrightness(value: boolean) {
        this.$set(this.currentSettings, 'noAutoBright', !value);
    }

    get previewImageStyle() {
        return {
            transform: `translate3d(${this.previewPanX}px, ${this.previewPanY}px, 0) scale(${this.previewZoom})`,
        };
    }

    @Watch('currentSettings', { deep: true })
    private settingsChanged() {
        if (this.syncingAll || this.phase !== 'advanced') return;
        if (this.applyToAll) this.copyCurrentToAll(false);
        this.schedulePreview();
    }

    private enqueue(request: ConfirmationRequest) {
        if (this.dialogOpen) {
            this.queuedRequests.push(request);
            return;
        }
        this.open(request);
    }

    private open(request: ConfirmationRequest) {
        this.rawCount = request.rawCount;
        this.rawFiles = request.files.filter(isRawFile);
        this.pendingResolve = request.resolve;
        this.dialogOpen = true;
        this.phase = 'confirm';
        this.advanced = false;
        this.applyToAll = false;
        this.currentIndex = 0;
        this.resetPreviewZoom();
        this.settingsByFile = new Map();
        this.rawFiles.forEach((file) => this.settingsByFile.set(file, Vue.observable(this.cloneDefaults())));
    }

    private async continueConversion() {
        if (!this.advanced) {
            this.checkingEmbeddedPreviews = true;
            let requiresLaboratory = false;

            try {
                for (const file of this.rawFiles) {
                    if (!await hasEmbeddedJpegPreview(file)) {
                        requiresLaboratory = true;
                        break;
                    }
                }
            } catch (error) {
                requiresLaboratory = true;
            } finally {
                this.checkingEmbeddedPreviews = false;
            }

            if (!this.dialogOpen || this.phase !== 'confirm') return;
            if (!requiresLaboratory) {
                this.finish(true);
                return;
            }

            this.advanced = true;
            this.phase = 'advanced';
            this.$nextTick(() => this.schedulePreview(0));
            return;
        }
        this.phase = 'advanced';
        this.$nextTick(() => this.schedulePreview(0));
    }

    private backToConfirmation() {
        this.clearPreviewTimer();
        this.phase = 'confirm';
    }

    private finish(confirmed: boolean) {
        const decision: RawConversionDecision = confirmed
            ? { confirmed: true, advanced: false }
            : CANCELLED_RAW_CONVERSION;
        this.complete(decision);
    }

    private finishAdvanced() {
        this.finalizing = true;
        this.complete({
            confirmed: true,
            advanced: true,
            settingsByFile: new Map(this.settingsByFile),
        });
    }

    private complete(decision: RawConversionDecision) {
        const resolve = this.pendingResolve;
        this.pendingResolve = null;
        this.dialogOpen = false;
        this.finalizing = false;
        this.clearPreviewTimer();
        this.revokePreview();
        if (resolve) resolve(decision);

        const nextRequest = this.queuedRequests.shift();
        if (nextRequest) this.$nextTick(() => this.open(nextRequest));
    }

    private selectFile(index: number) {
        if (index < 0 || index >= this.rawFiles.length || index === this.currentIndex) return;
        this.currentIndex = index;
        this.previewError = '';
        this.resetPreviewZoom();
        this.schedulePreview(0);
    }

    private zoomPreview(event: WheelEvent) {
        if (!this.previewUrl) return;
        const frame = this.$refs.rawPreviewFrame as HTMLElement | undefined;
        if (!frame) return;

        const previousZoom = this.previewZoom;
        const zoomFactor = event.deltaY < 0 ? 1.15 : 1 / 1.15;
        const nextZoom = Math.min(6, Math.max(1, previousZoom * zoomFactor));
        if (nextZoom === previousZoom) return;

        const bounds = frame.getBoundingClientRect();
        const cursorX = event.clientX - bounds.left - bounds.width / 2;
        const cursorY = event.clientY - bounds.top - bounds.height / 2;
        const ratio = nextZoom / previousZoom;

        this.previewPanX = cursorX - (cursorX - this.previewPanX) * ratio;
        this.previewPanY = cursorY - (cursorY - this.previewPanY) * ratio;
        this.previewZoom = nextZoom;
        this.clampPreviewPan();
    }

    private startPreviewDrag(event: MouseEvent) {
        if (!this.previewUrl || this.previewZoom <= 1 || event.button !== 0) return;
        event.preventDefault();
        this.previewDragging = true;
        this.previewDragStartX = event.clientX;
        this.previewDragStartY = event.clientY;
        this.previewDragOriginX = this.previewPanX;
        this.previewDragOriginY = this.previewPanY;
    }

    private movePreview(event: MouseEvent) {
        if (!this.previewDragging) return;
        this.previewPanX = this.previewDragOriginX + event.clientX - this.previewDragStartX;
        this.previewPanY = this.previewDragOriginY + event.clientY - this.previewDragStartY;
        this.clampPreviewPan();
    }

    private stopPreviewDrag() {
        this.previewDragging = false;
    }

    private resetPreviewZoom() {
        this.previewZoom = 1;
        this.previewPanX = 0;
        this.previewPanY = 0;
        this.previewDragging = false;
    }

    private clampPreviewPan() {
        const frame = this.$refs.rawPreviewFrame as HTMLElement | undefined;
        if (!frame || this.previewZoom <= 1) {
            this.previewPanX = 0;
            this.previewPanY = 0;
            return;
        }

        const maxX = frame.clientWidth * (this.previewZoom - 1) / 2;
        const maxY = frame.clientHeight * (this.previewZoom - 1) / 2;
        this.previewPanX = Math.max(-maxX, Math.min(maxX, this.previewPanX));
        this.previewPanY = Math.max(-maxY, Math.min(maxY, this.previewPanY));
    }

    private copyCurrentToAll(refresh = true) {
        if (!this.currentFile) return;
        const source = this.cloneSettings(this.currentSettings);
        this.syncingAll = true;
        this.rawFiles.forEach((file) => {
            if (file !== this.currentFile) this.settingsByFile.set(file, Vue.observable(this.cloneSettings(source)));
        });
        this.syncingAll = false;
        if (refresh) {
            this.settingsByFile = new Map(this.settingsByFile);
            this.schedulePreview();
        }
    }

    private resetCurrent() {
        if (!this.currentFile) return;
        this.settingsByFile.set(this.currentFile, Vue.observable(this.cloneDefaults()));
        this.settingsByFile = new Map(this.settingsByFile);
        if (this.applyToAll) this.copyCurrentToAll(false);
        this.schedulePreview(0);
    }

    private schedulePreview(delay = 500) {
        this.clearPreviewTimer();
        this.previewLoading = true;
        this.previewTimer = window.setTimeout(() => this.refreshPreview(), delay);
    }

    private async refreshPreview() {
        const file = this.currentFile;
        if (!file) return;
        const requestId = ++this.previewRequestId;
        const settings = this.cloneSettings(this.currentSettings);
        this.previewError = '';
        this.previewLoading = true;

        try {
            const blob = await createRawPreview(file, settings);
            if (requestId !== this.previewRequestId || file !== this.currentFile) return;
            const nextUrl = URL.createObjectURL(blob);
            this.revokePreview();
            this.previewUrl = nextUrl;
        } catch (error) {
            if (requestId !== this.previewRequestId) return;
            this.previewError = error instanceof Error ? error.message : 'Nie udało się przygotować podglądu.';
        } finally {
            if (requestId === this.previewRequestId) this.previewLoading = false;
        }
    }

    private clearPreviewTimer() {
        if (this.previewTimer !== null) {
            window.clearTimeout(this.previewTimer);
            this.previewTimer = null;
        }
    }

    private revokePreview() {
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = '';
    }

    private cloneDefaults(): RawDevelopSettings {
        return this.cloneSettings(DEFAULT_RAW_DEVELOP_SETTINGS);
    }

    private cloneSettings(settings: RawDevelopSettings): RawDevelopSettings {
        return {
            ...settings,
            userCblack: settings.userCblack ? [...settings.userCblack] as [number, number, number, number] : undefined,
        };
    }

    private fileKey(file: File, index: number) {
        return `${file.name}-${file.size}-${file.lastModified}-${index}`;
    }
}
